import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";
import { mediaSrc } from "@/lib/media";
import { useQuickView } from "@/components/QuickViewProvider";
import { FavoriteButton } from "@/components/FavoriteButton";

const BestSellers = () => {
  const { t } = useLang();
  const { contact } = useSite();
  const { products, open } = useQuickView();
  const scroller = useRef(null);
  const items = products.filter((p) => p.badge === "hit");

  if (items.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  const orderUrl = (p) =>
    `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(`${t("catalog.quote")} — ${p.title}`)}`;

  return (
    <section className="relative py-16 md:py-28 px-6 md:px-12 bg-background border-t border-border" data-testid="bestsellers">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <Eyebrow>{t("catalog.bestBadge")}</Eyebrow>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl text-foreground leading-tight">
                {t("catalog.bestTitle")}
              </h2>
              <p className="mt-4 text-muted-foreground font-sans leading-relaxed">{t("catalog.bestSubtitle")}</p>
            </div>
            {items.length > 3 && (
              <div className="hidden md:flex gap-2">
                <button
                  data-testid="bestsellers-prev"
                  onClick={() => scrollBy(-1)}
                  className="flex items-center justify-center w-11 h-11 rounded-full border border-border text-muted-foreground hover:border-gold hover:text-gold transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  data-testid="bestsellers-next"
                  onClick={() => scrollBy(1)}
                  className="flex items-center justify-center w-11 h-11 rounded-full border border-border text-muted-foreground hover:border-gold hover:text-gold transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </Reveal>

        <div
          ref={scroller}
          className="mt-10 flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-1 px-1 scrollbar-none"
          style={{ scrollbarWidth: "none" }}
          data-testid="bestsellers-track"
        >
          {items.map((p, i) => (
            <motion.div
              key={p.product_id}
              data-testid={`bestseller-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              onClick={() => open(p)}
              className="group snap-start shrink-0 w-[75vw] sm:w-[340px] bg-card border border-border hover:border-gold/50 rounded-sm overflow-hidden transition-colors flex flex-col cursor-pointer"
            >
              <div className="relative h-56 overflow-hidden bg-muted">
                {p.image ? (
                  <img
                    src={mediaSrc(p.image)}
                    alt={p.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : null}
                <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-widest bg-gold text-black px-2 py-1 rounded-sm">
                  {t("catalog.badges.hit")}
                </span>
                <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                  <FavoriteButton product={p} context="bs" className="w-9 h-9" size={16} />
                  <span className="text-[10px] uppercase tracking-widest bg-black/60 text-gold px-2 py-1 rounded-sm">
                    {t(`catalog.cats.${p.category}`)}
                  </span>
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-serif text-xl text-foreground">{p.title}</h3>
                {p.description ? (
                  <p className="mt-2 text-sm text-muted-foreground font-sans leading-relaxed line-clamp-2">
                    {p.description}
                  </p>
                ) : null}
                <div className="mt-auto pt-5 flex flex-col gap-2.5">
                  <a
                    data-testid={`bestseller-order-${i}`}
                    href={orderUrl(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center gap-2 bg-gold text-black font-sans font-semibold text-xs uppercase tracking-[0.12em] px-5 py-3 rounded-sm hover:bg-gold-deep transition-colors"
                  >
                    <MessageCircle size={15} /> {t("catalog.order")}
                  </a>
                  <button
                    onClick={(e) => { e.stopPropagation(); scrollToSection("contact"); }}
                    className="inline-flex items-center justify-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-[0.12em] text-gold hover:gap-2.5 transition-all"
                  >
                    {t("catalog.quote")} <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BestSellers;
